import { Helmet } from "react-helmet-async";

/**
 * PageSEO
 * - Adds strong page-level SEO
 * - Adds WebPage schema
 * - Adds Oglitz "Developed By" schema
 * - Optionally adds FAQ schema
 */
export default function PageSEO({
  title,
  description,
  canonical,
  pageName,
  faqs = [], // optional: [{ question, answer }]
}) {
  const faqSchema =
    faqs.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((faq) => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: faq.answer,
            },
          })),
        }
      : null;

  return (
    <Helmet>
      {/* ================= BASIC SEO ================= */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      {/* ================= OPEN GRAPH ================= */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Vision X Nexus" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta
        property="og:image"
        content="https://visionxnexus.com/visionxnexus-og-light.png"
      />

      {/* ================= TWITTER ================= */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta
        name="twitter:image"
        content="https://visionxnexus.com/visionxnexus-og-light.png"
      />

      {/* ================= WEBPAGE SCHEMA ================= */}
      <script type="application/ld+json">
        {JSON.stringify(
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: pageName,
            url: canonical,
            description: description,
            publisher: {
              "@type": "Organization",
              name: "Vision X Nexus Real Estate L.L.C",
              url: "https://visionxnexus.com",
            },
            creator: {
              "@type": "Organization",
              name: "Oglitz Software Pvt Ltd",
              url: "https://www.oglitz.com/",
              logo: "https://www.oglitz.com/static/media/Oglitz-main-icon.6ad376319eba01801745.png",
            },
          },
          null,
          2
        )}
      </script>

      {/* ================= OGLITZ ORGANIZATION SCHEMA ================= */}
      <script type="application/ld+json">
        {JSON.stringify(
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Oglitz Software Pvt Ltd",
            url: "https://www.oglitz.com/",
            logo: "https://www.oglitz.com/static/media/Oglitz-main-icon.6ad376319eba01801745.png",
            description:
              "Oglitz Software Pvt Ltd is a digital solutions company specializing in UI/UX design, website development, SEO, and scalable business applications.",
            sameAs: [
              "https://www.linkedin.com/company/oglitz/",
              "https://www.instagram.com/oglitzsoftware",
            ],
          },
          null,
          2
        )}
      </script>

      {/* ================= FAQ SCHEMA (OPTIONAL) ================= */}
      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema, null, 2)}
        </script>
      )}
    </Helmet>
  );
}
